import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, ArrowRight, Newspaper } from 'lucide-react'
import { BLOG_POSTS } from '../data/blogPosts'
import { blogPath } from '../lib/localizedUrl'
import bpEn from '../data/blogPosts.en.json'
import bpDe from '../data/blogPosts.de.json'
import bpPl from '../data/blogPosts.pl.json'
import bpSk from '../data/blogPosts.sk.json'
import bpIt from '../data/blogPosts.it.json'
import bpHu from '../data/blogPosts.hu.json'
import bpUk from '../data/blogPosts.uk.json'
import bpEs from '../data/blogPosts.es.json'
import bpFr from '../data/blogPosts.fr.json'
import bpNl from '../data/blogPosts.nl.json'
import bpJa from '../data/blogPosts.ja.json'
import { useSeoHead } from '../lib/seo'

const TRANS: Record<string, any[]> = { en: bpEn, de: bpDe, pl: bpPl, sk: bpSk, it: bpIt, hu: bpHu, uk: bpUk, es: bpEs, fr: bpFr, nl: bpNl, ja: bpJa }

export default function BlogPage() {
  const { t, i18n } = useTranslation()
  const tr = TRANS[i18n.language] || (i18n.language !== 'cs' && TRANS.en) || []
  const localize = (p: typeof BLOG_POSTS[number]) => {
    const x = tr.find((y) => y.slug === p.slug)
    return x ? { ...p, ...x } : p
  }
  useSeoHead({
    title: t('blog.meta_title', { defaultValue: 'Blog o SEO a GEO: návody a tipy | SEO Tools' }),
    description: t('blog.meta_desc', { defaultValue: 'Praktické články o SEO, GEO a optimalizaci pro AI vyhledávače. Návody, tipy a trendy.' }),
    canonicalPath: '/blog',
  })

  const posts = [...BLOG_POSTS].sort((a, b) => b.date.localeCompare(a.date)).map(localize)

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
          <Newspaper className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{t('blog.title', { defaultValue: 'Blog' })}</h1>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-8">{t('blog.subtitle', { defaultValue: 'Návody a tipy o SEO, GEO a optimalizaci pro AI vyhledávače.' })}</p>

      <div className="grid gap-5">
        {posts.map((p) => (
          <Link key={p.slug} to={blogPath(p.slug, i18n.language)}
            className="block bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-6 hover:border-primary-300 hover:shadow-sm transition group">
            <div className="flex flex-wrap gap-2 mb-2">
              {p.tags.map((tag: string) => (
                <span key={tag} className="px-2.5 py-0.5 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 text-xs font-semibold">{tag}</span>
              ))}
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary-600 transition">{p.title}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">{p.excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-gray-400 mt-4">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{new Date(p.date).toLocaleDateString('cs-CZ')}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{p.readMins} min</span>
              <span className="inline-flex items-center gap-1.5 text-primary-600 font-medium ml-auto group-hover:gap-2.5 transition-all">
                {t('blog.read', { defaultValue: 'Číst' })}<ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
